package common.frontend.api

import apps.App
import apps.AppReport
import device.DeviceReportSystem
import kotlinx.datetime.LocalDateTime
import kotlinx.serialization.Serializable
import org.ssoft.common.type.DashboardCategory
import org.ssoft.common.type.DashboardMsgType

object IoReport {

    @Serializable
    data class ListingDeviceSystem(
        val items: List<Item> = listOf()
    ) {
        @Serializable
        data class Item(
            var id: Long,
            val utc: LocalDateTime,
            val data: DeviceReportSystem
        )
    }

    @Serializable
    data class ListingDeviceAlive(
        val items: List<Item> = listOf()
    ) {
        @Serializable
        data class Item(
            var id: Long,
            var on: Boolean,
            val utc: LocalDateTime
        )
    }

    @Serializable
    data class ListingDeviceAliveMap(
        val map: Map<Long, Item> = mapOf()
    ) {
        @Serializable
        data class Item(
            var on: Boolean,
            val durationMs: Long
        )
    }

    /**
     * map of "campaignId" to "impressionCount"
     */
    @Serializable
    data class ListingCampaignSumTotal(
        val map: Map<Long, Int> = mapOf()
    )

    /**
     * map of "campaignId" to "impressionCount"
     */
    @Serializable
    data class ListingCampaignSumDeviceToday(
        val map: Map<Long, Int> = mapOf()
    )

    @Serializable
    data class ReportCounter(
        var items: List<List<Long>> = listOf()
    )

    @Serializable
    data class ReportImpression(
        var counter: LongArray = longArrayOf(),
        var stamp: LongArray = longArrayOf(),
    ) {
        override fun equals(other: Any?): Boolean {
            if (this === other) return true
            if (other !is ReportImpression) return false

            if (!counter.contentEquals(other.counter)) return false
            if (!stamp.contentEquals(other.stamp)) return false

            return true
        }

        override fun hashCode(): Int {
            var result = counter.contentHashCode()
            result = 31 * result + stamp.contentHashCode()
            return result
        }
    }

    @Serializable
    data class ReportAppQuiz(
        var questions: Map<Int, App.Quiz.QuestionStack> = mapOf(),
        var answers: List<AnswerEntry> = listOf(),
    ) {

        companion object;

        @Serializable
        data class AnswerEntry(
            var id: Long,
            var utc: LocalDateTime,
            var revision: Int,
            var data: AppReport.Quiz
        )

        @Serializable
        data class AnswerHolder(
            var items: List<AnswerEntry> = listOf()
        )
    }

    @Serializable
    data class GameWheelHistory(
        var id: Long,
        var items: List<Item> = listOf(),
    ) {

        companion object;

        @Serializable
        data class Item(
            var idItem: Long,
            var idGroup: Long,
            var code: String,
            var dt: LocalDateTime
        )
    }

    @Serializable
    data class GameWheelStatus(
        var id: Long,
        var items: List<Item> = listOf(),
    ) {

        companion object;

        @Serializable
        data class Item(
            var id: Long,
            var counter: Int
        )
    }

    @Serializable
    data class Dashboard(
        val campaign: Campaign = Campaign(),
        val device: Device = Device(),
        val company: Company = Company()
    ) {
        @Serializable
        data class Campaign(
            val counter: CampaignCounter = CampaignCounter(),
            val messages: List<Message> = emptyList()
        )

        @Serializable
        data class Company(
            val counter: CompanyCounter = CompanyCounter(),
            val messages: List<Message> = emptyList()
        )

        @Serializable
        data class CompanyCounter(
            val storageUsed: Long = 0,
            val storageUnused: Long = 0,
            val storageTrash: Long = 0,
            val storageFree: Long = 0,
            val storageTotal: Long = 0,
        )
        @Serializable
        data class CampaignCounter(
            val global: CampaignCounterDetails = CampaignCounterDetails(),
            val today: CampaignCounterDetails = CampaignCounterDetails(),
        )

        @Serializable
        data class Message(
            val serverId: Long = -1,
            val companyId: Long = -1,
            val locationId: Long = -1,
            val deviceId: Long = -1,
            val campaignGroupId: Long = -1,
            /**
             * @see DashboardCategory
             */
            val category: Int = DashboardCategory.UNDEFINED.mask,
            /**
             * @see DashboardMsgType
             */
            val type: Int = DashboardMsgType.UNDEFINED.mask,

            val name1: String = "",
            val name2: String = ""
        )

        @Serializable
        data class CampaignCounterDetails(
            val active: Int = 0,
            val published: Int = 0,
            val willFinish: Int = 0,

            val started: Int = 0,
            val draft: Int = 0,
            val paused: Int = 0,
            val stop: Int = 0,

            val edited: Int = 0,
            val finished: Int = 0,
            val deleted: Int = 0
        )

        @Serializable
        data class Device(
            val counter: DeviceCounter = DeviceCounter(),
            val messages: List<Message> = emptyList()
        )

        @Serializable
        data class DeviceCounter(
            val count: Int = 0,
            val layerTop: Int = 0,
            val layerBottom: Int = 0,
            val layerBoth: Int = 0,
            val disconnected: Int = 0,
            val overheating: Int = 0,
            val lowStorage: Int = 0,
            val noCampaigns: Int = 0,
            val noApps: Int = 0,
            val updateRequired: Int = 0,
            val campaignWithoutImpression: Int = 0,
            val displayOff: Int = 0
        )
    }
}
